<template>
    <transition name="radial">
        <div v-if="visible" class="radial">
            <div class="radial__backdrop" @click="$emit('close')"
                @contextmenu.prevent></div>
            <div class="radial__items">
                <button v-for="(item, index) in items" :key="item.key || index" type="button"
                    class="radial__item" :class="{ 'radial__item--active': item.active, 'radial__item--text': !item.icon }"
                    :style="itemStyle(index)" :disabled="item.disabled"
                    :aria-label="item.ariaLabel || item.label" @click="onSelect(item)">
                    <i v-if="item.icon" :class="item.icon"></i>
                    <span v-else class="radial__label">{{ item.label }}</span>
                </button>
            </div>
        </div>
    </transition>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
    visible: {
        type: Boolean,
        default: () => false,
    },
    // Each item: { key, icon?, label?, ariaLabel?, active?, disabled? }
    items: {
        type: Array,
        default: () => [],
    },
    // Distance of items from the anchor centre, in px.
    radius: {
        type: Number,
        default: () => 150,
    },
    // Arc the items are spread over. Angles are in degrees measured clockwise
    // from the positive x-axis (0 = right, 90 = down). Defaults fan toward the
    // lower-right so the menu stays on screen for a top-left anchor.
    startAngle: {
        type: Number,
        default: () => 12,
    },
    endAngle: {
        type: Number,
        default: () => 108,
    },
});

const emit = defineEmits(['select', 'close']);

const itemStyle = (index) => {
    const count = props.items.length;
    const span = props.endAngle - props.startAngle;
    const angle = count <= 1
        ? props.startAngle + span / 2
        : props.startAngle + (span * index) / (count - 1);
    const radians = (angle * Math.PI) / 180;
    const x = Math.cos(radians) * props.radius;
    const y = Math.sin(radians) * props.radius;
    return { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` };
};

const onSelect = (item) => {
    if (item.disabled) return;
    emit('select', item);
};
</script>

<style scoped>
.radial {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1100;
}

.radial__backdrop {
    position: fixed;
    inset: 0;
    z-index: -1;
    background-color: rgba(0, 0, 0, 0.15);
}

.radial__item {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    border: none;
    background-color: #000000;
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.radial__item:hover:not(:disabled) {
    background-color: #333333;
}

.radial__item--active {
    background-color: #ffc34e;
    color: #000000;
}

.radial__item:disabled {
    opacity: 0.4;
    cursor: default;
}

.radial__item .pi:before {
    font-size: 1.8rem;
    font-weight: 900;
}

.radial__label {
    font-size: 1.1rem;
    font-weight: 700;
}

/* Pop-in / fade transition for the whole menu. */
.radial-enter-active,
.radial-leave-active {
    transition: opacity 0.2s ease;
}

.radial-enter-from,
.radial-leave-to {
    opacity: 0;
}
</style>
